import React from 'react';
import { useSelector } from 'react-redux';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import TextField from '@material-ui/core/TextField';
import RemainCountTextField from 'editor/components/FormContent/Field/RemainCountTextField';
import withSettings from 'editor/components/FormContent/Field/withSettings';
import useFocus from 'client/components/FormContent/Field/fieldState';
import {
    fieldWrapperStyle,
    optionWrapperStyle,
    optionTextStyle,
    optionTextAlignStyle,
    optionImageWrapperStyle,
    optionImageCoverWrapperStyle,
    imgOptionsBlockWidthStyle,
    generateMuiTextFieldCustomColorStyle
} from 'common/style/fields.style';
import { gridStyle, colStyle, colCell } from 'common/style/grid';
import { generateSectionStyle } from 'common/style/cssStyleType';
import { error } from 'common/components/Mui/styles/color';
import NameDescription from 'common/components/NameDescription';
import { OPTIONS_TYPE, getOptionLimitCount } from 'common/utils/config';
import { setTextVariantStyle } from 'common/utils/variantStyle';

function FieldImageChoice({
    fieldId,
    displayName,
    description,
    textAlign = 0,
    rowItemsPC = 4,
    rowItemsMobile = 2,
    isRequired,
    multiChoice,
    enableOptionsLimit,
    showRest,
    imageCropType,
    settingStyle,
    error,
    value,
    updateFormValue,
    touched,
    setFieldTouched,
    handleBlur
}) {
    const [focus, toggleError] = useFocus(false);
    const { displayNameStyle, descStyle, optionStyle, answerStyle } = generateSectionStyle(
        settingStyle
    );
    const options = useSelector(state => {
        return state.options.reduce((arr, option) => {
            const optionSettings = state.settings.options.find(item => item.id === option.id) || {};
            const cloneOption = {
                ...option,
                imageUrl: optionSettings?.imageUrl ?? option.imageUrl,
                setting: { submissionLimit: optionSettings?.submissionLimit || 0 }
            };
            if ((error?.optionId ?? []).find(id => id === option.id)) {
                cloneOption.error = error.message;
            }
            if (option.fieldId === fieldId && !option.deleted) {
                arr.push(cloneOption);
            }
            return arr;
        }, []);
    });

    const optionsSettings = useSelector(state => {
        return state.settings.options.filter(optionSetting => {
            return options.find(option => optionSetting.id === option.id);
        });
    });

    const selectedOptions = value[fieldId] || (multiChoice ? [] : {});
    const hasSelectedOptions = multiChoice
        ? selectedOptions.length > 0
        : selectedOptions.optionId !== undefined;

    const isSelected = option => {
        if (multiChoice) {
            return Boolean(selectedOptions.find(s => s.optionId === option.id));
        } else {
            return option.id === selectedOptions.optionId;
        }
    };

    const getPayload = option => {
        const optionSetting = optionsSettings.find(
            optionsSetting => optionsSetting.id === option.id
        );

        const imageUrl = optionSetting ? optionSetting.imageUrl : '';
        return { label: option.label, imageUrl: imageUrl, optionId: option.id };
    };

    const handleOptionClick = option => {
        const payload = getPayload(option);
        if (option.type === OPTIONS_TYPE.NORMAL_OTHER) {
            payload.label = '';
        }
        if (multiChoice) {
            const newSelectedOptions = selectedOptions.filter(s => s.optionId !== option.id);
            if (newSelectedOptions.length === selectedOptions.length) {
                newSelectedOptions.push(payload);
            }
            updateFormValue({ [fieldId]: newSelectedOptions });
        } else {
            updateFormValue({ [fieldId]: payload });
        }
        setFieldTouched(fieldId);
    };

    const handleOtherInputChange = (e, option) => {
        if (option.type === OPTIONS_TYPE.NORMAL_OTHER) {
            const payload = {
                label: e.target.value,
                optionId: option.id
            };
            if (multiChoice) {
                const newSelectedOptions = selectedOptions.map(s =>
                    s.optionId === option.id ? payload : s
                );
                updateFormValue({ [fieldId]: newSelectedOptions });
            } else {
                updateFormValue({ [fieldId]: payload });
            }
        }
    };

    const showFieldError = !focus && touched && error && typeof error === 'string';

    return (
        <div css={css(fieldWrapperStyle)} id={`section_${fieldId}`}>
            <NameDescription
                displayName={displayName}
                description={description}
                displayNameStyle={displayNameStyle}
                descStyle={descStyle}
                isRequired={isRequired}
            />
            <div
                css={css(gridStyle, imgOptionsBlockWidthStyle(rowItemsPC), {
                    rowGap: 8
                })}
            >
                {options.map(option => {
                    const isSelectedOption = isSelected(option);
                    const isOtherOption = option.type === OPTIONS_TYPE.NORMAL_OTHER;
                    const optionId = option.id;
                    const isReachLimit =
                        option.type === OPTIONS_TYPE.NORMAL &&
                        enableOptionsLimit &&
                        getOptionLimitCount(option) <= 0;
                    const isError = option.error;
                    return (
                        <div
                            css={css(
                                colStyle,
                                colCell({
                                    pc: rowItemsPC || 4,
                                    mb: rowItemsMobile || 2
                                }),
                                Boolean(isError || isReachLimit) && disabledStyle
                            )}
                            key={optionId}
                        >
                            <div
                                css={[
                                    optionWrapperStyle,
                                    optionStyle,
                                    imageChoiceOptionWrapperStyle,
                                    hasSelectedOptions
                                        ? isSelectedOption
                                            ? selectedStyle(answerStyle.color)
                                            : blurStyle
                                        : '',
                                    Boolean(isError || isReachLimit) && disableClickStyle,
                                    isError && errorStyle
                                ]}
                                onClick={() => handleOptionClick(option)}
                            >
                                <div css={css(optionImageWrapperStyle)}>
                                    {imageCropType === 0 && (
                                        <div
                                            css={css(optionImageCoverWrapperStyle(option.imageUrl))}
                                        />
                                    )}
                                    {imageCropType === 1 && (
                                        <div
                                            css={css(
                                                optionImageCoverWrapperStyle(option.imageUrl),
                                                `border-radius: 50%;`
                                            )}
                                        />
                                    )}
                                    {imageCropType === 2 && (
                                        <img src={option.imageUrl} alt={option.label} />
                                    )}
                                </div>
                                {isOtherOption && isSelectedOption ? (
                                    <TextField
                                        css={css(generateMuiTextFieldCustomColorStyle(answerStyle))}
                                        placeholder={i18next.t('section.answer.short.placeholder')}
                                        onFocus={() => toggleError(true)}
                                        onBlur={e => toggleError(false, handleBlur, e)}
                                        onChange={e => handleOtherInputChange(e, option)}
                                        value={
                                            multiChoice
                                                ? selectedOptions.find(
                                                      s => s.optionId === option.id
                                                  )?.label || ''
                                                : value[fieldId]?.label || ''
                                        }
                                        error={showFieldError}
                                        onClick={e => e.stopPropagation()}
                                        autoFocus
                                    />
                                ) : (
                                    <div
                                        css={[
                                            css(optionTextStyle),
                                            css(optionTextAlignStyle(textAlign)),
                                            imageChoiceOptionStyle
                                        ]}
                                    >
                                        {option.label}
                                    </div>
                                )}
                                {enableOptionsLimit && showRest && (
                                    <RemainCountTextField
                                        option={option}
                                        isError={!!option?.error}
                                    />
                                )}
                            </div>
                            {option.error && (
                                <span css={css(errorTextStyle)} id="feedback">
                                    {option.error}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
            {showFieldError && (
                <div css={css(errorTextStyle)} id="feedback">
                    {error}
                </div>
            )}
        </div>
    );
}

FieldImageChoice.propTypes = {
    fieldId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    displayName: PropTypes.string,
    description: PropTypes.string,
    textAlign: PropTypes.number,
    rowItemsPC: PropTypes.number,
    rowItemsMobile: PropTypes.number,
    imageCropType: PropTypes.number,
    error: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            message: PropTypes.string,
            optionId: PropTypes.arrayOf(PropTypes.number)
        })
    ]),
    touched: PropTypes.object,
    updateFormValue: PropTypes.func,
    multiChoice: PropTypes.bool,
    setFieldTouched: PropTypes.func,
    settingStyle: PropTypes.object,
    isRequired: PropTypes.bool,
    value: PropTypes.object,
    handleBlur: PropTypes.func
};

export default withSettings(FieldImageChoice);

const blurStyle = `
    opacity: 0.5;
`;

const disableClickStyle = css`
    opacity: 0.3;
    pointer-events: none;
`;

const disabledStyle = css`
    cursor: not-allowed;
`;

const errorStyle = `
    border-color: ${error.color};
`;

const selectedStyle = color => `
    border-color: ${color};
`;

const errorTextStyle = `
    text-align: right;
    font-size: 0.8125rem;
    line-height: 1.1875rem;
    color: ${error.color};
    float: right;
`;

const imageChoiceOptionStyle = css`
    margin: 0px;
    white-space: pre-wrap;
    ${setTextVariantStyle('subtitle1')}
`;

const imageChoiceOptionWrapperStyle = css`
    cursor: pointer;
`;
